import { getMarkupAssigneeName } from "@pages/project-details/project-markups/markup-assignee-utils";
import { getMarkupCreatorName } from "@pages/project-details/project-markups/markup-details-utils";
import { sortMarkupTags } from "@pages/project-details/project-markups/markup-tags-sort";
import { MARKUP_STATUS_ORDER, ProjectMarkupsTableHeaders } from "@pages/project-details/project-markups/project-markups-table-utils";
import { getMarkupLastUpdate, getSetMarkupField } from "@pages/project-details/project-markups/sidepanel/markup-sidepanel-activity-utils";
import { getMarkupStatusValue } from "@pages/project-details/project-markups/status/markup-status-utils";

/** Columns the annotations can be sorted by, the same in all the views */

/**
 * Sort of the annotations before the user chooses one: the next due first.
 * Its column is visible by default, as the table always shows the column it is sorted by.
 */
export const DEFAULT_MARKUPS_SORT = {
  field: ProjectMarkupsTableHeaders.dueDate,
  sort: "asc"
};

/** Options of the sort dropdown, in the order they are listed */
export const MARKUPS_SORT_OPTIONS = {
  [ProjectMarkupsTableHeaders.name]: {
    field: ProjectMarkupsTableHeaders.name,
    label: "Name"
  },
  [ProjectMarkupsTableHeaders.dueDate]: {
    field: ProjectMarkupsTableHeaders.dueDate,
    label: "Due date"
  },
  [ProjectMarkupsTableHeaders.status]: {
    field: ProjectMarkupsTableHeaders.status,
    label: "Status"
  },
  [ProjectMarkupsTableHeaders.assignee]: {
    field: ProjectMarkupsTableHeaders.assignee,
    label: "Assignee"
  },
  [ProjectMarkupsTableHeaders.creator]: {
    field: ProjectMarkupsTableHeaders.creator,
    label: "Created by"
  },
  [ProjectMarkupsTableHeaders.tags]: {
    field: ProjectMarkupsTableHeaders.tags,
    label: "Tags"
  },
  [ProjectMarkupsTableHeaders.attachments]: {
    field: ProjectMarkupsTableHeaders.attachments,
    label: "Attachments"
  },
  [ProjectMarkupsTableHeaders.createdAt]: {
    field: ProjectMarkupsTableHeaders.createdAt,
    label: "Created"
  },
  [ProjectMarkupsTableHeaders.lastUpdatedAt]: {
    field: ProjectMarkupsTableHeaders.lastUpdatedAt,
    label: "Last updated"
  },
  [ProjectMarkupsTableHeaders.assignedAt]: {
    field: ProjectMarkupsTableHeaders.assignedAt,
    label: "Assigned"
  },
  [ProjectMarkupsTableHeaders.dueDateSetAt]: {
    field: ProjectMarkupsTableHeaders.dueDateSetAt,
    label: "Due date set"
  }
};

/** Members that the names of the assignees and creators are taken from */

/** Value a markup is sorted by, undefined when the markup has none */

/** Returns whether the value is one of the sort fields */
export function isMarkupsSortField(value) {
  return Object.keys(MARKUPS_SORT_OPTIONS).includes(value);
}

/** Returns the time of the date in milliseconds, or undefined when there is no date */
function toTime(date) {
  return date ? Date.parse(date) : undefined;
}

/** Returns the value of the markup that it is sorted by in the field */
function getMarkupSortValue(markup, field, {
  companyMembers,
  projectMembers
}) {
  switch (field) {
    case ProjectMarkupsTableHeaders.name:
      return markup.name;
    case ProjectMarkupsTableHeaders.assignee:
      return getMarkupAssigneeName({
        assignee: markup.assignee,
        companyMembers,
        projectMembers
      });
    case ProjectMarkupsTableHeaders.creator:
      return getMarkupCreatorName({
        creatorId: markup.createdBy,
        companyMembers,
        projectMembers
      });
    case ProjectMarkupsTableHeaders.dueDate:
      return toTime(markup.dueDate?.value);
    case ProjectMarkupsTableHeaders.status:
      return MARKUP_STATUS_ORDER.indexOf(getMarkupStatusValue(markup.status?.value));
    case ProjectMarkupsTableHeaders.tags:
      return sortMarkupTags(markup.labels).map(({
        name
      }) => name).join(", ") || undefined;
    case ProjectMarkupsTableHeaders.attachments:
      return markup.attachmentsCount;
    case ProjectMarkupsTableHeaders.createdAt:
      return toTime(markup.createdAt);
    case ProjectMarkupsTableHeaders.lastUpdatedAt:
      return toTime(getMarkupLastUpdate(markup).modifiedAt);
    case ProjectMarkupsTableHeaders.assignedAt:
      return toTime(getSetMarkupField(markup, "assignee")?.modifiedAt);
    case ProjectMarkupsTableHeaders.dueDateSetAt:
      return toTime(getSetMarkupField(markup, "dueDate")?.modifiedAt);
  }
}

/** Compares two values, in the direction, leaving the ones without a value at the end */
function compareSortValues(value1, value2, direction) {
  if (value1 === undefined || value2 === undefined) {
    return Number(value1 === undefined) - Number(value2 === undefined);
  }
  if (typeof value1 === "string" && typeof value2 === "string") {
    return direction * value1.localeCompare(value2, undefined, {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- attribute from another lib
      numeric: true,
      sensitivity: "base"
    });
  }
  return direction * (Number(value1) - Number(value2));
}

/** Returns the markups sorted by the field, the same order for all the views */
export function sortMarkups(markups, {
  field,
  sort
}, members) {
  const values = new Map(markups.map(markup => [markup.id, getMarkupSortValue(markup, field, members)]));
  const direction = sort === "asc" ? 1 : -1;
  return [...markups].sort((markup1, markup2) => compareSortValues(values.get(markup1.id), values.get(markup2.id), direction));
}

/** Returns the sort stored in the query param, e.g. "Due Date:asc", or the default one */
export function parseMarkupsSort(value) {
  const [field = "", sort] = value?.split(":") ?? [];
  if (!isMarkupsSortField(field) || sort !== "asc" && sort !== "desc") {
    return DEFAULT_MARKUPS_SORT;
  }
  return {
    field,
    sort
  };
}

/** Returns the sort as stored in the query param */
export function stringifyMarkupsSort({
  field,
  sort
}) {
  return `${field}:${sort}`;
}